<template>
  <div class="viewContainer receiptMatcherView">
    <div class="receiptMatcherHeader">
      <h1>{{ $t('receiptMatcher.title') }}</h1>
      <div class="receiptMatcherControls">
        <b-button-group class="toggleButtons sheetTypeToggle">
          <b-button @click="selectedSheetType = 'outbound'" :class="selectedSheetType === 'outbound' ? 'active' : ''">
            {{ $t('receiptMatcher.outbound') }}
          </b-button>
          <b-button @click="selectedSheetType = 'inbound'" :class="selectedSheetType === 'inbound' ? 'active' : ''">
            {{ $t('receiptMatcher.inbound') }}
          </b-button>
        </b-button-group>

        <b-form-select v-model="selectedMonth" :options="monthOptions" class="filterInput" />
        <b-form-select v-model="selectedYear" :options="yearOptions" class="filterInput" />
      </div>
    </div>

    <div class="receiptMatcherTables">
      <!-- TABLE: Documents -->
      <div class="receiptMatcherTableContainer">
        <div class="tableHeader">
          <h2>{{ documentsTitle }}</h2>
          <b-form-input v-model="searchTerm" :placeholder="$t('receiptMatcher.searchDocuments')"
            class="tableSearch wineSearch" />
        </div>
        <div class="tableScrollContainer">
          <b-table ref="documentTable" :items="filteredDocuments" :fields="documentFields" small sticky-header
            selectable select-mode="single" @row-clicked="openDocument">
            <template #cell(uploadDate)="data">
              <span>{{ data.value }}</span>
            </template>
            <template #cell(status)="data">
              <DocumentStatusBadge :rows="data.item.rows" />
            </template>
            <template #cell(download)="data">
              <button v-if="data.item.driveFileId" class="iconButton download"
                :title="$t('receiptMatcher.download')" @click.stop="downloadDocument(data.item)">
                <DownloadIcon />
              </button>
            </template>
          </b-table>
          <LoadingAnimation v-if="loadingDocuments" />
          <div v-if="showDocumentsHelp" class="sheetRowsHelp">
            {{ $t('receiptMatcher.documentsHelp') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AiService from '@/services/PoolinqAiService'
import LoadingAnimation from '@/components/LoadingAnimation.vue'
import DownloadIcon from '@/components/icons/DownloadIcon.vue'
import DocumentStatusBadge from '@/components/receiptmatcher/DocumentStatusBadge.vue'

export default {
  name: 'Receipts',
  components: {
    LoadingAnimation,
    DownloadIcon,
    DocumentStatusBadge,
  },
  data() {
    return {
      selectedSheetType: this.getInitialSheetType(),
      selectedMonth: this.getInitialMonth(),
      selectedYear: this.getInitialYear(),
      documents: [],
      searchTerm: '',
      loadingDocuments: false,
    }
  },
  computed: {
    ...mapGetters(['getVenue']),
    documentsTitle() {
      const docType = this.selectedSheetType === 'outbound'
        ? this.$t('receiptMatcher.outbound_short')
        : this.$t('receiptMatcher.inbound_short')
      return `${docType}`
    },
    monthOptions() {
      const months = []
      for (let i = 0; i < 12; i++) {
        const date = new Date(2000, i, 1)
        months.push({
          value: i,
          text: date.toLocaleDateString(this.$i18n.locale, { month: 'long' }),
        })
      }
      return months
    },
    yearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let y = currentYear; y >= currentYear - 5; y--) {
        years.push({ value: y, text: String(y) })
      }
      return years
    },
    documentFields() {
      return [
        { key: 'invoiceNumber', label: this.$t('receiptMatcher.invoiceNumber'), sortable: true },
        { key: 'uploadDate', label: this.$t('receiptMatcher.uploadDate'), sortable: true },
        { key: 'download', label: this.$t('receiptMatcher.downloadOutbound'), sortable: false },
        { key: 'status', label: this.$t('receiptMatcher.status'), sortable: false },
      ]
    },
    filteredDocuments() {
      if (!this.searchTerm || this.searchTerm.trim() === '') {
        return this.documents
      }
      const term = this.searchTerm.trim().toLowerCase()
      return this.documents.filter((doc) => {
        return Object.values(doc).some((value) => {
          if (value == null) return false
          return String(value).toLowerCase().includes(term)
        })
      })
    },
    showDocumentsHelp() {
      return !this.loadingDocuments && this.documents.length === 0
    },
  },
  methods: {
    /**
     * Reads the initial sheet type from the URL query param, falling back
     * to 'outbound' when absent or invalid.
     */
    getInitialSheetType() {
      const sheetType = this.$route.query.sheet_type
      return sheetType === 'inbound' ? 'inbound' : 'outbound'
    },
    /**
     * Reads the initial month (0-based) from the URL query param, falling
     * back to the current month when absent or invalid.
     */
    getInitialMonth() {
      const monthParam = parseInt(this.$route.query.month, 10)
      if (!isNaN(monthParam) && monthParam >= 0 && monthParam <= 11) {
        return monthParam
      }
      return new Date().getMonth()
    },
    /**
     * Reads the initial year from the URL query param, falling back to the
     * current year when absent or invalid.
     */
    getInitialYear() {
      const yearParam = parseInt(this.$route.query.year, 10)
      const currentYear = new Date().getFullYear()
      if (!isNaN(yearParam) && yearParam >= 2000 && yearParam <= currentYear) {
        return yearParam
      }
      return currentYear
    },
    async loadDocuments() {
      this.loadingDocuments = true
      this.documents = []
      const venueId = this.getVenue.id
      try {
        const monthName = new Date(2000, this.selectedMonth, 1)
          .toLocaleDateString('en-US', { month: 'long' })
        let response
        if (this.selectedSheetType === 'outbound') {
          response = await AiService.getAllOutboundSheetRowsOfMonth(venueId, monthName, String(this.selectedYear))
        } else {
          response = await AiService.getAllInboundSheetRowsOfMonth(venueId, monthName, String(this.selectedYear))
        }
        this.documents = this.groupDocumentsByInvoiceNumber(response)
      } catch (err) {
        console.log(err)
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.loadError'),
          variant: 'danger',
        })
      } finally {
        this.loadingDocuments = false
      }
    },
    /**
     * Groups the sheet rows returned by the month endpoints into documents,
     * one per distinct invoice number. Each document carries the invoice
     * number, the upload date and the rows belonging to it.
     */
    groupDocumentsByInvoiceNumber(response) {
      const data = response && response.data ? response.data : []
      let rows = []
      let sourceFiles = []
      if (Array.isArray(data)) {
        rows = data
      } else if (Array.isArray(data.sheets)) {
        data.sheets.forEach((sheet) => {
          if (Array.isArray(sheet.rows)) {
            rows = rows.concat(sheet.rows)
          }
          if (sheet.sourceFiles) {
            sourceFiles = sourceFiles.concat(sheet.sourceFiles)
          }
        })
      } else if (Array.isArray(data.rows)) {
        rows = data.rows
      } else if (Array.isArray(data.data)) {
        rows = data.data
      }

      // Map source files by invoice number so each document group can carry
      // its Google Drive file id for downloading.
      const sourceFileByInvoice = new Map()
      sourceFiles.forEach((sf) => {
        if (sf && sf.invoiceNumber && sf.driveFileId) {
          sourceFileByInvoice.set(sf.invoiceNumber, sf.driveFileId)
        }
      })

      const byInvoice = new Map()
      rows.forEach((row) => {
        const invoiceNumber = row.invoiceNumber || row.invoice_number || 'n/a'
        if (!byInvoice.has(invoiceNumber)) {
          byInvoice.set(invoiceNumber, {
            invoiceNumber,
            uploadDate: row.uploadDate || row.documentDate || null,
            driveFileId: sourceFileByInvoice.get(invoiceNumber) || null,
            rows: [],
          })
        }
        byInvoice.get(invoiceNumber).rows.push(row)
      })
      return Array.from(byInvoice.values())
    },
    /**
     * Downloads the source document for a document group using its Google
     * Drive file id. The file extension is derived from the actual file
     * (MIME type, then the Content-Disposition filename, then .pdf).
     */
    async downloadDocument(doc) {
      const driveFileId = doc.driveFileId
      if (!driveFileId) return
      try {
        const response = await AiService.downloadDocument(driveFileId, this.getVenue.id)
        const blob = new Blob([response.data])
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${doc.invoiceNumber}${this.getFileExtension(response)}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (err) {
        console.log(err)
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.downloadError'),
          variant: 'danger',
        })
      }
    },
    /**
     * Derives the file extension (including the leading dot) from the
     * download response. Priority:
     *   1. The Content-Disposition header filename, if present.
     *   2. The blob MIME type, mapped to a known extension.
     *   3. '.pdf' as a last resort.
     */
    getFileExtension(response) {
      const disposition = response && response.headers && response.headers['content-disposition']
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/i)
        if (match && match[1]) {
          const ext = match[1].split('.').pop()
          if (ext && ext !== match[1]) {
            return `.${ext}`
          }
        }
      }
      const mime = response && response.data && response.data.type
      if (mime) {
        const mimeExt = {
          'application/pdf': '.pdf',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
          'application/vnd.ms-excel': '.xls',
          'text/csv': '.csv',
          'text/plain': '.txt',
          'application/zip': '.zip',
          'application/json': '.json',
        }[mime]
        if (mimeExt) return mimeExt
      }
      return '.pdf'
    },
    /**
     * Opens the ReceiptMatcher view for the clicked document, passing the
     * sheet type and the invoice number/date derived from its rows.
     */
    openDocument(document) {
      const query = {
        pvid: this.getVenue.id,
        sheet_type: this.selectedSheetType,
      }
      if (this.selectedSheetType === 'outbound') {
        const firstRow = document.rows[0]
        const receiptDate = firstRow && (firstRow.documentDate || firstRow.uploadDate)
        if (receiptDate) {
          query.receipt_date = this.toDatepickerFormat(receiptDate)
        }
      } else {
        query.invoice_number = document.invoiceNumber
        const firstRow = document.rows[0]
        const invoiceDate = firstRow && (firstRow.documentDate || firstRow.uploadDate)
        if (invoiceDate) {
          query.invoice_date = this.toDatepickerFormat(invoiceDate)
        }
      }
      this.$router.push({ name: 'ReceiptMatcher', query })
    },
    /**
     * Converts a date to 'YYYY-MM-DD' (the format expected by the
     * <b-form-datepicker> input).
     *
     * The date can arrive in two formats:
     *   - 'DD-MM-YYYY' from a URL query param (e.g. invoice_date=06-07-2026)
     *   - 'YYYY-MM-DD' already in the datepicker format
     *
     * Only the 'DD-MM-YYYY' form is converted; an already-correct 'YYYY-MM-DD'
     * value is returned unchanged. Returns an empty string if the input is
     * empty or invalid.
     */
    toDatepickerFormat(date) {
      if (!date) return ''
      // Normalize dot separators (e.g. '19.08.2026') to dashes
      const normalized = String(date).replace(/\./g, '-')
      const parts = normalized.split('-')
      if (parts.length !== 3) return date
      // 'DD-MM-YYYY' → 'YYYY-MM-DD' (day first, 2 digits)
      if (parts[0].length === 2 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`
      }
      // Already 'YYYY-MM-DD' - leave as-is
      return normalized
    },
    /**
     * Keeps the month/year selection in sync with the browser URL query
     * params so the current view is shareable/bookmarkable.
     */
    syncUrlParams() {
      const query = {
        ...this.$route.query,
        sheet_type: this.selectedSheetType,
        month: String(this.selectedMonth),
        year: String(this.selectedYear),
      }
      this.$router.replace({ query })
    },
  },
  async created() {
    await this.loadDocuments()
  },
  watch: {
    selectedSheetType() {
      this.syncUrlParams()
      this.loadDocuments()
    },
    selectedMonth() {
      this.syncUrlParams()
      this.loadDocuments()
    },
    selectedYear() {
      this.syncUrlParams()
      this.loadDocuments()
    },
  },
}
</script>

<style scoped>
.receiptMatcherView {
  /* Do NOT set padding here - the .viewContainer already provides left padding
     for the navigation menu and right margin. Overriding it pushes the table
     behind the left menu. */
  padding-top: 20px;
  padding-bottom: 20px;
}

.receiptMatcherHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.receiptMatcherControls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-right: 22px;
}

.sheetTypeToggle {
  /* The global .toggleButtons styles (main.css) provide the base look.
     This scoped rule highlights the active button, like the
     .winesViewHeader .toggleButtons.wineViewFilter button.active rule. */
  width: 290px;
  height: 32px;
}

.sheetTypeToggle button.active {
  background-color: #495057;
  color: var(--color-primary-500);
}

.filterInput {
  width: 220px;
}

.receiptMatcherTables {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.receiptMatcherTableContainer {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.tableScrollContainer {
  /* Foreground, Background */
  scrollbar-color: var(--color-primary-500) #0d0d0d;
  position: relative;
}

/* Overlay the loading indicator on top of the table area instead of pushing
   it below the fixed-height table. */
.tableScrollContainer .dotfloatingContainer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
}

.tableScrollContainer .b-table-sticky-header {
  /* Fixed height so both tables always have the same height, even when the
     sheet rows table is empty or still loading. */
  height: calc(100vh - 380px);
  height: calc(100dvh - 380px);
  overflow-y: auto;
  /* Extra space after the last row so it can still be scrolled to the top */
  padding-bottom: 300px;
}

.tableScrollContainer::-webkit-scrollbar,
.tableScrollContainer .b-table-sticky-header::-webkit-scrollbar {
  width: 10px;
  /* Mostly for vertical scrollbars */
}

.tableScrollContainer::-webkit-scrollbar-thumb,
.tableScrollContainer .b-table-sticky-header::-webkit-scrollbar-thumb {
  /* Foreground */
  background: var(--color-primary-500);
  border-radius: 8px;
}

.tableScrollContainer::-webkit-scrollbar-track,
.tableScrollContainer .b-table-sticky-header::-webkit-scrollbar-track {
  /* Background */
  background: #0d0d0d;
  border-radius: 8px;
}

.tableScrollContainer .table {
  margin-bottom: 0;
}

.venueWineFooter {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-top: 20px;
  border-top: 1px solid #333;
}

.sheetRowsHelp {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: 200px;
  color: #888;
  font-size: 1rem;
  text-align: center;
  padding: 0 100px;
}

/* Alternating row background colors (same as VenueWines.vue) */
.receiptMatcherView ::v-deep table tr:nth-child(odd) td {
  background-color: #2b2c2d;
}

/* The download button uses the global .iconButton base look but renders its
   icon via the DownloadIcon component, so hide the empty :before pseudo-icon
   that the global rule injects. */
.receiptMatcherView button.iconButton.download:before {
  content: none;
}

.receiptMatcherView button.iconButton.download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
}

.tableHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 10px;
}

.tableHeader h2 {
  margin: 0;
  font-size: 1.2rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.tableSearch {
  min-width: 200px;
  max-width: 300px;
  color: var(--color-gray-150);
  background-color: var(--color-gray-300);
  height: 36px;
  max-height: 36px;
  margin-right: 22px;
}

.tableSearch::placeholder {
  color: var(--color-gray-150);
}

.detailGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  padding: 10px;
}

.detailItem {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #eee;
  padding: 4px 0;
}

.detailLabel {
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
}

.detailValue {
  font-size: 0.9rem;
}
</style>
